'use client';

import * as React from 'react';
import type { DataGridColumn } from '@heroui-pro/react';
import { Avatar, Chip } from '@heroui/react';
import {
  PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID,
  type PageDataGridTableFieldMeta,
} from './page-data-grid-types';

function avatarFallbackLabel(original: Record<string, unknown>): string {
  const nick = original.nickname;
  const user = original.username;
  const displayName =
    typeof nick === 'string' && nick.trim()
      ? nick
      : typeof user === 'string' && user.trim()
        ? user
        : '?';
  if (/[\u4e00-\u9fa5]/.test(displayName)) return displayName.charAt(0).toUpperCase();
  return displayName.substring(0, 2).toUpperCase();
}

const variantToChipColor: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'accent'> = {
  default: 'default',
  secondary: 'accent',
  destructive: 'danger',
  outline: 'default',
  success: 'success',
  warning: 'warning',
  error: 'danger',
};

function renderCellByMeta<TData extends object>(
  field: PageDataGridTableFieldMeta,
  value: unknown,
  original: TData
): React.ReactNode | undefined {
  const cc = field.columnComponent;
  if (!cc) return undefined;

  const row = original as Record<string, unknown>;

  if (cc === 'Avatar') {
    const url = value as string | null | undefined;
    const username = String(row.username ?? '');
    return (
      <Avatar size="sm" variant="soft">
        {url ? <Avatar.Image alt={username} src={url} /> : null}
        <Avatar.Fallback>{avatarFallbackLabel(row)}</Avatar.Fallback>
      </Avatar>
    );
  }

  if (cc === 'Badge' && field.options?.length) {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    const key = String(value);
    const idx = field.options.findIndex((o) => o.value === key);
    const label = field.options[idx]?.label ?? key;
    const variantList = field.variants;
    const variantName =
      idx >= 0 && variantList && variantList[idx] ? variantList[idx] : 'default';
    const color = variantToChipColor[variantName] ?? 'default';
    return (
      <Chip color={color} size="sm" variant="soft">
        <Chip.Label>{label}</Chip.Label>
      </Chip>
    );
  }

  return undefined;
}

function formatTableCell(
  value: unknown,
  options?: PageDataGridTableFieldMeta['options']
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (options?.length) {
    const key = String(value);
    const hit = options.find((o) => o.value === key);
    return hit?.label ?? key;
  }
  if (typeof value === 'boolean') {
    return value ? '是' : '否';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T?\d{0,2}:?\d{0,2}:?\d{0,2}/.test(value) || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      }
    }
    return value;
  }
  return String(value);
}

export function buildHeroUIDataGridColumns<TData extends object>(
  fields: PageDataGridTableFieldMeta[],
  options?: {
    columnOverrides?: Partial<Record<string, Partial<DataGridColumn<TData>>>>;
    actionsRender?: (ctx: { row: { original: TData } }) => React.ReactNode;
    /** 与 DataGrid 列宽拖拽配合：数据列默认可调，选择/操作列在 PageDataGrid 内单独处理 */
    defaultAllowsResizing?: boolean;
  }
): DataGridColumn<TData>[] {
  const { columnOverrides, actionsRender, defaultAllowsResizing = true } = options ?? {};

  const dataCols: DataGridColumn<TData>[] = fields.map((field) => {
    const accessorKey = field.fieldName as keyof TData & string;

    const minW =
      field.columnMinWidth != null && Number.isFinite(field.columnMinWidth)
        ? Math.max(96, field.columnMinWidth)
        : 96;

    const base: DataGridColumn<TData> = {
      id: field.fieldName,
      accessorKey,
      header: field.label,
      allowsSorting: field.sortable === true,
      allowsResizing: defaultAllowsResizing,
      minWidth: minW,
      cell: (item) => {
        const v = item[accessorKey] as unknown;
        const byMeta = renderCellByMeta(field, v, item);
        if (byMeta !== undefined) return byMeta;
        return formatTableCell(v, field.options);
      },
    };

    const ov = columnOverrides?.[field.fieldName];
    const merged = ov ? { ...base, ...ov } : base;
    const innerCell = merged.cell;
    if (innerCell) {
      merged.cell = (item, column) => (
        <div className="min-w-0 max-w-full overflow-hidden break-all whitespace-normal">
          {innerCell(item, column)}
        </div>
      );
    }
    return merged;
  });

  if (!actionsRender) return dataCols;

  const actionsCol: DataGridColumn<TData> = {
    id: PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID,
    header: '',
    align: 'end',
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    allowsSorting: false,
    allowsResizing: false,
    pinned: 'end',
    cell: (item) => (
      <div className="flex min-w-0 justify-end gap-1 overflow-hidden">{actionsRender({ row: { original: item } })}</div>
    ),
  };

  return [...dataCols, actionsCol];
}
